// @ts-check

/**
 * @file Renders the task create and edit screen.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import Text from "@kaspernj/api-maker/build/utils/text"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {useLocalSearchParams, useRouter} from "expo-router"
import {createRef, memo, useEffect} from "react"
import useBreakpoint from "responsive-breakpoints/build/use-breakpoint.js"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {Pressable, ScrollView, View} from "react-native-propforge"
import Button from "../../components/button"
import buildRoutePath from "../../shared/build-route-path"
import Card from "../../components/card"
import errorMessage from "../../shared/error-message"
import FormHtmlEditor from "../../components/form/form-html-editor"
import {FormHtmlEditorUploadSession} from "../../components/form/form-html-editor/upload-session"
import FormSelect from "../../components/form/form-select"
import FormTextInput from "../../components/form/form-text-input"
import ProjectsSelect from "../../components/projects/select"
import {Project} from "../../frontend-models/project"
import {Task} from "../../frontend-models/task"
import {TaskBoard} from "../../frontend-models/task-board"
import {optionalRouteParam} from "../../shared/route-params"
import {taskStateOptions} from "../../shared/task-state"
import {userFromProfileCommandResponse} from "../../auth/use-current-user"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {User} from "../../frontend-models/user"
import {useScreenLoading} from "../../components/board-layout/screen-loading"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import Board from "../../frontend-models/board" */
/** @import BoardColumn from "../../frontend-models/board-column" */
/** @import TaskType from "../../frontend-models/task-type" */
/** @typedef {{text: string, value: string}} SelectOption */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} TasksEditScreenProps */

/**
 * @typedef {object} TasksEditScreenState
 * @property {BoardColumn[]} boardColumns Board columns available for the selected project.
 * @property {string} currentUserId Current signed-in user identifier.
 * @property {boolean} loading Whether initial task data is loading.
 * @property {boolean} saving Whether the form is submitting.
 * @property {string} selectedBoardColumnId Selected board column identifier.
 * @property {string} selectedProjectId Selected project identifier.
 * @property {string} selectedPriority Selected priority value.
 * @property {string} selectedState Selected task state.
 * @property {string} selectedTaskTypeId Selected task type identifier.
 * @property {{kind: "error" | "success", text: string} | null} status Status message shown in the form.
 * @property {Task | null} task Existing task being edited.
 * @property {TaskType[]} taskTypes Task types available for the selected project.
 */

/**
 * Renders the task create and edit form.
 * @augments {ShapeComponent<TasksEditScreenProps, TasksEditScreenState>}
 */
class TasksEditScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useBreakpoint>} */
  breakpoint
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  projectOptionsLoadRequestId = 0
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {string | null} */
  taskID
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  descriptionUploadSession = new FormHtmlEditorUploadSession()
  formObjectRef = createRef()
  /** @type {TasksEditScreenState} */
  state = {
    boardColumns: /** @type {BoardColumn[]} */ ([]),
    currentUserId: "",
    loading: true,
    saving: false,
    selectedBoardColumnId: "",
    selectedProjectId: "",
    selectedPriority: "1",
    selectedState: "open",
    selectedTaskTypeId: "",
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null,
    task: /** @type {Task | null} */ null,
    taskTypes: /** @type {TaskType[]} */ ([])
  }

  /**
   * Initializes translations, route params, and task data loading.
   * @returns {void} Nothing.
   */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()
    const breakpoint = useBreakpoint()
    const colors = useThemeColors()
    const themeName = useThemeName()

    const taskID = optionalRouteParam(params, "id", "Task ID")
    const routeProjectID = optionalRouteParam(params, "projectId", "Project ID")
    this.breakpoint = breakpoint
    this.params = params
    this.router = router
    this.taskID = taskID
    this.colors = colors
    this.themeName = themeName
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          const [defaultProject, currentProfileResponse] = await Promise.all([
            Project
              .query()
              .select(["id", "name"])
              .order([["name", "asc"], ["id", "asc"]])
              .limit(1)
              .first(),
            User.currentProfile()
          ])
          if (cancelled) return
          const task = taskID
            ? await Task.find(taskID)
            : null
          if (cancelled) return
          const selectedProjectId = task?.projectId()
            ? String(task.projectId())
            : routeProjectID
              ? routeProjectID
              : defaultProject
                ? String(defaultProject.id())
                : ""
          const selectedPriority =
            task?.priority() != null ? String(task.priority()) : "1"
          const selectedState = task?.state() || "open"
          const selectedTaskTypeId = task?.taskTypeId()
            ? String(task.taskTypeId())
            : ""
          const selectedBoardColumnId = ""

          const currentProfileUser = userFromProfileCommandResponse(currentProfileResponse)
          this.s.currentUserId = currentProfileUser
            ? String(currentProfileUser.id)
            : ""
          this.s.selectedProjectId = selectedProjectId
          this.s.selectedPriority = selectedPriority
          this.s.selectedState = selectedState
          this.s.selectedTaskTypeId = selectedTaskTypeId
          this.s.selectedBoardColumnId = selectedBoardColumnId
          this.s.task = task

          await this.loadProjectScopedOptions(selectedProjectId, {
            selectedBoardColumnId,
            selectedTaskTypeId
          })
          if (cancelled) return
          this.s.loading = false
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [routeProjectID, taskID])
  }

  /**
   * Renders the task form screen.
   * @returns {ReactNode} Task form content.
   */
  render() {
    const {
      boardColumns,
      loading,
      saving,
      selectedBoardColumnId,
      selectedPriority,
      selectedState,
      selectedProjectId,
      selectedTaskTypeId,
      status,
      task,
      taskTypes
    } = this.s
    const {taskID} = this.tt
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="taskEditScreen"
        />
      )
    }
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const {breakpoint} = this.tt
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="taskEditScreen"
      >
        <ScrollView
          contentContainerStyle={styles[`scrollContent-${breakpoint.mdUp}`] ||= breakpoint.styling({
            base: {
              alignItems: "stretch",
              paddingBottom: 24,
              paddingLeft: 24,
              paddingRight: 24,
              paddingTop: 24
            },
            mdUp: {
              alignItems: "stretch",
              paddingBottom: 48,
              paddingLeft: 24,
              paddingRight: 24,
              paddingTop: 24
            }
          })}
          style={styles.scroll ||= {
            flex: 1
          }}
          testID="taskEditScreen/scroll"
        >
          <View testID="taskEditScreen/content">
            <Text
              style={styles[`screenTitle-${themeName}`] ||= {
                fontSize: 24,
                fontWeight: 700,
                marginBottom: 16
              }}
              testID="taskEditScreen/title"
            >
              {taskID ? _("Edit task") : _("New task")}
            </Text>
            <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
              <Card
                style={styles.formCardSpacing ||= {
                  gap: 12,
                  maxWidth: 520,
                  width: "100%"
                }}
                testID="taskEditScreen/form"
              >
                <FormTextInput
                  defaultValue={task ? task.name() || "" : ""}
                  label={_("Name")}
                  name="name"
                  testID="taskEditScreen/nameInput"
                />
                <FormHtmlEditor
                  defaultValue={task ? task.description() || "" : ""}
                  label={_("Description")}
                  name="description"
                  testID="taskEditScreen/descriptionInput"
                  uploadSession={this.tt.descriptionUploadSession}
                />
                <ProjectsSelect
                  defaultValue={selectedProjectId || undefined}
                  label={_("Project")}
                  name="projectId"
                  onChangeValue={this.tt.onProjectChangeValue}
                  testID="taskEditScreen/projectInput"
                />
                <FormSelect
                  defaultValue={selectedTaskTypeId}
                  label={_("Task type")}
                  name="taskTypeId"
                  onChangeValue={this.tt.onTaskTypeChangeValue}
                  options={[
                    {text: _("No task type"), value: ""},
                    ...taskTypes.map((taskType) => ({
                      text: String(taskType.name() || _("Unnamed task type")),
                      value: String(taskType.id())
                    }))
                  ]}
                  search
                  testID="taskEditScreen/taskTypeInput"
                />
                <View testID="taskEditScreen/stateInput">
                  <Text
                    style={styles[`fieldLabel-${themeName}`] ||= {
                      color: colors.textMuted,
                      fontWeight: 600
                    }}
                    testID="taskEditScreen/stateInput/label"
                  >
                    {_("State")}
                  </Text>
                  <View
                    style={styles.optionGroup ||= {
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 8,
                      marginTop: 6
                    }}
                    testID="taskEditScreen/stateOptions"
                  >
                    {taskStateOptions().map((stateOption) => (
                      <Pressable
                        key={stateOption.value}
                        onPress={this.tt.onStateOptionPress(stateOption.value)}
                        style={styles[`stateOption-${themeName}-${selectedState === stateOption.value}`] ||= optionPressableStyle(selectedState === stateOption.value, colors)}
                        testID={`taskEditScreen/stateOption-${stateOption.value}`}
                      >
                        <View
                          style={styles.stateOptionIcon ||= {
                            marginRight: 8,
                            width: 14
                          }}
                          testID={`taskEditScreen/stateOption-${stateOption.value}/icon`}
                        >
                          <FontAwesome6
                            color={selectedState === stateOption.value ? colors.link : colors.text}
                            name="circle-check"
                            size={13}
                          />
                        </View>
                        <Text
                          style={styles[`stateOptionText-${themeName}-${selectedState === stateOption.value}`] ||= optionTextStyle(selectedState === stateOption.value, colors)}
                          testID={`taskEditScreen/stateOption-${stateOption.value}/text`}
                        >
                          {stateOption.text}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                </View>
                <View testID="taskEditScreen/priorityInput">
                  <Text
                    style={styles[`fieldLabel-${themeName}`]}
                    testID="taskEditScreen/priorityInput/label"
                  >
                    {_("Priority")}
                  </Text>
                  <View
                    style={styles.optionGroup}
                    testID="taskEditScreen/priorityOptions"
                  >
                    {priorityOptions().map((priorityOption) => (
                      <Pressable
                        key={priorityOption.value}
                        onPress={this.tt.onPriorityOptionPress(priorityOption.value)}
                        style={styles[`priorityOption-${themeName}-${selectedPriority === priorityOption.value}`] ||= {
                          ...optionPressableStyle(selectedPriority === priorityOption.value, colors),
                          alignItems: "center",
                          minWidth: 44
                        }}
                        testID={`taskEditScreen/priorityOption-${priorityOption.value}`}
                      >
                        <View
                          style={styles.priorityOptionIcon ||= {
                            marginRight: 8,
                            width: 14
                          }}
                          testID={`taskEditScreen/priorityOption-${priorityOption.value}/icon`}
                        >
                          <FontAwesome6
                            color={selectedPriority === priorityOption.value ? colors.link : colors.text}
                            name="flag"
                            size={13}
                          />
                        </View>
                        <Text
                          style={styles[`priorityOptionText-${themeName}-${selectedPriority === priorityOption.value}`] ||= optionTextStyle(selectedPriority === priorityOption.value, colors)}
                          testID={`taskEditScreen/priorityOption-${priorityOption.value}/text`}
                        >
                          {priorityOption.text}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                </View>
                {!task &&
                  <FormSelect
                    defaultValue={selectedBoardColumnId}
                    label={_("Column")}
                    name="boardColumnId"
                    onChangeValue={this.tt.onBoardColumnChangeValue}
                    options={[
                      {text: _("No board column"), value: ""},
                      ...boardColumns.map((boardColumn) => ({
                        text: boardColumn.name() || _("Column"),
                        value: String(boardColumn.id())
                      }))
                    ]}
                    search
                    testID="taskEditScreen/boardColumnInput"
                  />
                }
                {status &&
                  <Text
                    style={styles[`status-${themeName}-${status.kind}`] ||= {
                      color: status.kind === "success" ? colors.successText : colors.dangerText,
                      fontWeight: 600
                    }}
                    testID="taskEditScreen/statusMessage"
                  >
                    {status.text}
                  </Text>
                }
                <Button
                  disabled={saving}
                  iconName="floppy-disk"
                  label={saving ? _("Saving...") : _("Save")}
                  onPress={this.tt.onSubmit}
                  testID="taskEditScreen/saveButton"
                />
              </Card>
            </Form>
          </View>
        </ScrollView>
      </View>
    )
  }

  /**
   * Loads project-scoped board column and task type options.
   * @param {string} projectID Project identifier to load options for.
   * @param {{selectedBoardColumnId?: string, selectedTaskTypeId?: string}} [options] Existing selections to preserve when still valid.
   * @returns {Promise<void>} Resolves after project-scoped options are loaded.
   */
  async loadProjectScopedOptions(
    projectID,
    {selectedBoardColumnId = "", selectedTaskTypeId = ""} = {}
  ) {
    const requestId = ++this.projectOptionsLoadRequestId

    if (!projectID) {
      this.s.boardColumns = []
      this.s.selectedBoardColumnId = ""
      this.s.selectedTaskTypeId = ""
      this.s.taskTypes = []
      return
    }

    const project = await Project
      .where({id: projectID})
      .preload({
        boards: {boardColumns: true},
        taskTypes: {translations: true}
      })
      .first()
    /** @type {Board | null} */
    const board = project ? project.boardsLoaded()[0] || null : null
    const boardColumns = board
      ? board
        .boardColumnsLoaded()
        .slice()
        .sort((left, right) => (left.position() || 0) - (right.position() || 0))
      : []
    const taskTypes = project ? project.taskTypesLoaded() : []

    if (requestId !== this.projectOptionsLoadRequestId) return

    const sortedTaskTypes = taskTypes.sort((taskTypeA, taskTypeB) =>
      String(taskTypeA.name() || "").localeCompare(String(taskTypeB.name() || ""))
    )
    const defaultTaskType = sortedTaskTypes.find((taskType) => taskType.defaultForProject())
    const nextTaskTypeId = sortedTaskTypes.some(
      (taskType) => String(taskType.id()) === selectedTaskTypeId
    )
      ? selectedTaskTypeId
      : defaultTaskType
        ? String(defaultTaskType.id())
        : ""
    const nextBoardColumnId = boardColumns.some(
      (boardColumn) => String(boardColumn.id()) === selectedBoardColumnId
    )
      ? selectedBoardColumnId
      : ""

    this.s.boardColumns = boardColumns
    this.s.selectedBoardColumnId = nextBoardColumnId
    this.s.selectedTaskTypeId = nextTaskTypeId
    this.s.taskTypes = sortedTaskTypes

    const formObject = this.formObjectRef.current

    if (formObject) {
      formObject.setValue("boardColumnId", nextBoardColumnId)
      formObject.setValue("projectId", projectID)
      formObject.setValue("taskTypeId", nextTaskTypeId)
    }
  }

  /**
   * Applies the selected board column.
   * @param {string} boardColumnId Selected board column identifier.
   * @returns {void} Nothing.
   */
  onBoardColumnChangeValue = (boardColumnId) => {
    this.s.selectedBoardColumnId = boardColumnId
  }

  /**
   * Applies the selected project and reloads scoped options.
   * @param {string} projectID Selected project identifier.
   * @returns {void} Nothing.
   */
  onProjectChangeValue = (projectID) => {
    this.s.selectedBoardColumnId = ""
    this.s.selectedProjectId = projectID
    this.s.selectedTaskTypeId = ""
    void this.loadProjectScopedOptions(projectID)
  }

  /**
   * Builds a priority option press handler.
   * @param {string} priority Selected priority value.
   * @returns {() => void} Press handler.
   */
  onPriorityOptionPress = (priority) => () => {
    this.s.selectedPriority = priority
  }

  /**
   * Builds a state option press handler.
   * @param {string} state Selected state value.
   * @returns {() => void} Press handler.
   */
  onStateOptionPress = (state) => () => {
    this.s.selectedState = state
  }

  /**
   * Submits task form values.
   * @returns {Promise<void>} Resolves after save or validation handling.
   * @rejects {Error} When the form is not mounted or save cleanup fails.
   */
  onSubmit = async () => {
    const {params, router, taskID} = this.tt
    const {currentUserId, selectedPriority, selectedState, task} = this.s
    const formObject = this.formObjectRef.current

    if (!formObject) {
      throw new Error("TasksEditScreen requires an Api Maker Form")
    }

    const values = formObject.asObject()
    const projectID = values.projectId || this.s.selectedProjectId

    this.s.saving = true
    this.s.status = null
    /** @type {Task | null} */
    let createdTask = null

    try {
      const rawDescription = String(values.description || "")
      const parsedCurrentUserId = currentUserId || null
      const attributes = {
        name: values.name || "",
        priority: selectedPriority ? Number.parseInt(selectedPriority, 10) : null,
        projectId: projectID || null,
        state: selectedState || "",
        taskTypeId: values.taskTypeId || null,
        userId: task?.userId()
          ? task.userId()
          : parsedCurrentUserId
      }
      /** @type {Task} */
      let targetTask
      const uploadedFilesAttributes = this.tt.descriptionUploadSession.nestedUploadedFilesAttributes(rawDescription, parsedCurrentUserId)
      const descriptionForSave = uploadedFilesAttributes.length > 0
        ? rawDescription
        : this.tt.descriptionUploadSession.flushNestedUploadedFilesHtml({html: rawDescription, uploadedFiles: []})

      if (task && taskID) {
        targetTask = await task.update({
          ...attributes,
          description: descriptionForSave,
          ...(uploadedFilesAttributes.length > 0 ? {uploadedFilesAttributes} : {})
        })
      } else {
        createdTask = await Task.create({
          ...attributes,
          description: descriptionForSave,
          ...(uploadedFilesAttributes.length > 0 ? {uploadedFilesAttributes} : {})
        })
        targetTask = createdTask
      }

      if (uploadedFilesAttributes.length > 0) {
        const description = this.tt.descriptionUploadSession.flushNestedUploadedFilesHtml({
          html: rawDescription,
          uploadedFiles: targetTask.uploadedFilesLoaded()
        })

        if (description !== targetTask.description()) {
          targetTask = await targetTask.update({description})
        }
      }

      if (!task) {
        try {
          await this.persistTaskBoard({
            selectedBoardColumnId: values.boardColumnId || "",
            taskID: String(targetTask.id())
          })
        } catch (error) {
          if (createdTask?.id()) {
            try {
              await createdTask.destroy()
              createdTask = null
            } catch (_destroyError) {
              // Keep the task-board error visible to the user.
            }
          }
          throw error
        }
      }
      const targetProjectID = targetTask.projectId() || null
      const targetProjectQuery = targetProjectID ? `?projectId=${encodeURIComponent(targetProjectID)}` : ""
      router.push(buildRoutePath(params, `/tasks/${targetTask.id()}${targetProjectQuery}`))
    } catch (error) {
      if (createdTask?.id()) {
        try {
          await createdTask.destroy()
        } catch (_destroyError) {
          // Keep the original save error visible to the user.
        }
      }
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }

  /**
   * Applies the selected task type.
   * @param {string} taskTypeID Selected task type identifier.
   * @returns {void} Nothing.
   */
  onTaskTypeChangeValue = (taskTypeID) => {
    this.s.selectedTaskTypeId = taskTypeID
  }

  /**
   * Persists the task board assignment for the saved task.
   * @param {{selectedBoardColumnId: string, taskID: string}} args Assignment values.
   * @returns {Promise<void>} Resolves after the task board assignment is saved.
   */
  async persistTaskBoard({selectedBoardColumnId, taskID}) {
    if (!selectedBoardColumnId) return

    await TaskBoard.place({
      boardColumnId: selectedBoardColumnId,
      taskId: taskID
    })
  }
}

/** @type {NamedExoticComponent<TasksEditScreenProps>} */
const TasksEditScreenComponent = memo(shapeComponent(TasksEditScreen))

export default TasksEditScreenComponent

/**
 * Builds selectable priority options.
 * @returns {SelectOption[]} Priority select options.
 */
function priorityOptions() {
  return [1, 2, 3, 4, 5].map((priorityValue) => ({
    text: String(priorityValue),
    value: String(priorityValue)
  }))
}

/**
 * Builds a state/priority option pressable style from the active palette.
 * @param {boolean} selected Whether the option is currently selected.
 * @param {import("../../shared/theme").BoardThemeColors} colors Active palette.
 * @returns {object} Pressable style for state/priority option pills.
 */
function optionPressableStyle(selected, colors) {
  return {
    alignItems: "center",
    backgroundColor: selected ? colors.infoBackground : colors.surface,
    borderColor: selected ? colors.info : colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: "row",
    paddingHorizontal: 12,
    paddingVertical: 10
  }
}

/**
 * Builds the matching text style for a state/priority option label.
 * @param {boolean} selected Whether the option is currently selected.
 * @param {import("../../shared/theme").BoardThemeColors} colors Active palette.
 * @returns {object} Text style for state/priority option labels.
 */
function optionTextStyle(selected, colors) {
  return {
    color: selected ? colors.link : colors.text,
    fontWeight: 600
  }
}
